import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { db, auth } from "./firebase";
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { jsPDF } from "jspdf";

const BASE_URL = "https://menu-qrmenu.web.app";

function QRCodeGenerator() {
  const [locali, setLocali] = useState([]);
  const [localeSelezionato, setLocaleSelezionato] = useState("");
  const [datiLocale, setDatiLocale] = useState(null);
  const [saleSelezionate, setSaleSelezionate] = useState({ principale: true });
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [tokensMap, setTokensMap] = useState({});
  const [loadingTokens, setLoadingTokens] = useState(false);
  const navigate = useNavigate();

  // 🔹 Carica i locali all'avvio
  useEffect(() => {
    const fetchLocali = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "locali"));
        const tags = [];
        querySnapshot.forEach((doc) => tags.push(doc.id));
        setLocali(tags);
      } catch (error) {
        console.error("Errore nel caricamento dei locali:", error);
      }
    };
    fetchLocali();
  }, []);

  // 🔹 Carica i dati del locale selezionato
  useEffect(() => {
    const fetchDatiLocale = async () => {
      if (!localeSelezionato) return;
      try {
        const docRef = doc(db, "locali", localeSelezionato);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setDatiLocale(docSnap.data());
          const nuoveSale = { principale: true };
          const sale = docSnap.data().sale || [];
          sale.forEach((sala) => {
            nuoveSale[sala.lettera] = true;
          });
          setSaleSelezionate(nuoveSale);
        } else {
          setDatiLocale(null);
        }
      } catch (error) {
        console.error("Errore nel caricamento del locale:", error);
      }
    };
    fetchDatiLocale();
  }, [localeSelezionato]);

  // 🏛️ Genera lista completa tavoli (principale + sale selezionate)
  const generaListaTavoli = () => {
    if (!datiLocale) return [];
    const tavoli = [];

    if (saleSelezionate.principale) {
      const numTavoli = datiLocale.numeroTavoli || datiLocale.tavoli || 0;
      for (let i = 1; i <= numTavoli; i++) {
        tavoli.push({ id: `${i}`, label: `Tavolo ${i}`, sala: "principale" });
      }
    }

    const sale = datiLocale.sale || [];
    sale.forEach((sala) => {
      if (saleSelezionate[sala.lettera]) {
        for (let i = 1; i <= sala.numeroTavoli; i++) {
          tavoli.push({
            id: `${sala.lettera}${i}`,
            label: `Tavolo ${sala.lettera}${i}`,
            sala: sala.lettera,
          });
        }
      }
    });

    return tavoli;
  };

  // Helper per generare token casuale
  const generaTokenCasuale = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  // Helper per inizializzare il token del tavolo su Firestore
  const inizializzaTokenTavolo = async (localeTag, tableId) => {
    try {
      const tavoloRef = doc(db, "locali", localeTag, "tavoli", tableId);
      const tavoloSnap = await getDoc(tavoloRef);

      let tokenAttuale;

      if (!tavoloSnap.exists()) {
        tokenAttuale = generaTokenCasuale();
        await setDoc(tavoloRef, {
          numero: tableId,
          tokenAttuale,
          creatoIl: serverTimestamp(),
        });
      } else {
        tokenAttuale = tavoloSnap.data().tokenAttuale;
        // Se per qualche motivo il token manca, ne genera uno nuovo
        if (!tokenAttuale) {
          tokenAttuale = generaTokenCasuale();
          await setDoc(tavoloRef, { tokenAttuale }, { merge: true });
        }
      }

      return tokenAttuale;
    } catch (error) {
      console.error(`Errore token tavolo ${tableId}:`, error);
      return generaTokenCasuale();
    }
  };

  // ✅ Costruisce l'URL corretto per il QR: /menu/{locale}/{tavolo}?token={token}
  const buildQRUrl = (tavoloId) => {
    return `${BASE_URL}/table-redirect?table=${tavoloId}&locale=${localeSelezionato}`;
  };

  // ✅ URL asporto
  const buildAsportoUrl = () => {
    return `${BASE_URL}/menu-asporto/${localeSelezionato}`;
  };

  // 🔹 Scarica un singolo QR come SVG
  const handleDownload = (tavoloId) => {
    const svg = document.getElementById(`qr-${tavoloId}`);
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const imageSrc = "data:image/svg+xml;base64," + svg64;

    const link = document.createElement("a");
    link.href = imageSrc;
    link.download = `${localeSelezionato}_tavolo_${tavoloId}.svg`;
    link.click();
  };

  // 📄 Scarica tutti i QR in un unico PDF (max 12 QR per pagina, 3 colonne x 4 righe)
  const handleDownloadPDF = async () => {
    if (!datiLocale) return;
    setLoadingPDF(true);

    const tavoli = generaListaTavoli();
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    // Layout: 3 colonne x 4 righe = 12 card per pagina
    const colonne = 3;
    const righe = 4;
    const qrPerPagina = colonne * righe;

    // Dimensioni pagina A4: 210 x 297 mm
    const pageW = 210;
    const pageH = 297;
    const marginX = 8;
    const marginY = 8;
    const gapX = 4;  // spazio tra card orizzontale
    const gapY = 4;  // spazio tra card verticale

    // Larghezza/altezza card con gap espliciti
    const cardW = (pageW - marginX * 2 - gapX * (colonne - 1)) / colonne;
    const cardH = (pageH - marginY * 2 - gapY * (righe - 1)) / righe;

    // QR: 72% della larghezza card
    const qrSize = Math.floor(cardW * 0.72);
    const textH = 7;
    const contentH = qrSize + 4 + textH;

    for (let i = 0; i < tavoli.length; i++) {
      const tavolo = tavoli[i];
      const svg = document.getElementById(`qr-${tavolo.id}`);
      if (!svg) continue;

      const xml = new XMLSerializer().serializeToString(svg);
      const svg64 = btoa(unescape(encodeURIComponent(xml)));
      const imageSrc = "data:image/svg+xml;base64," + svg64;

      const img = new Image();
      img.src = imageSrc;
      await new Promise((res) => (img.onload = res));

      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 512, 512);
      const pngData = canvas.toDataURL("image/png");

      const idx = i % qrPerPagina;
      const col = idx % colonne;
      const row = Math.floor(idx / colonne);

      // Angolo in alto a sinistra della card (con gap tra card)
      const cardX = marginX + col * (cardW + gapX);
      const cardY = marginY + row * (cardH + gapY);

      // Bordo card sottile
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.2);
      pdf.roundedRect(cardX, cardY, cardW, cardH, 3, 3, "S");

      // QR centrato orizzontalmente nella card
      // Verticalmente: il blocco (qrSize + gap + textH) è centrato nella card
      const offsetY = (cardH - contentH) / 2;
      const qrX = cardX + (cardW - qrSize) / 2;
      const qrY = cardY + offsetY;

      pdf.addImage(pngData, "PNG", qrX, qrY, qrSize, qrSize);

      // Etichetta centrata sotto il QR
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(30, 30, 30);
      pdf.text(tavolo.label, cardX + cardW / 2, qrY + qrSize + 5, {
        align: "center",
      });

      if (idx === qrPerPagina - 1 && i < tavoli.length - 1) pdf.addPage();
    }

    pdf.save(`${localeSelezionato}_QR_Tavoli.pdf`);
    setLoadingPDF(false);
  };

  // 🔹 Carica i token per tutti i tavoli visibili
  useEffect(() => {
    const generaTokensTavoli = async () => {
      if (!localeSelezionato || !datiLocale) return;
      setLoadingTokens(true);

      const tavoli = generaListaTavoli();
      const tokens = {};

      for (const tavolo of tavoli) {
        const token = await inizializzaTokenTavolo(localeSelezionato, tavolo.id);
        tokens[tavolo.id] = token;
      }

      setTokensMap(tokens);
      setLoadingTokens(false);
    };

    generaTokensTavoli();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeSelezionato, datiLocale, saleSelezionate]);

  return (
    <div>
      <Navbar />
      <div className="page-with-navbar container">
        <h2>📲 Generatore QR</h2>
        <p>Seleziona un locale registrato per generare i QR dei tavoli.</p>

        <label>Locale:</label>
        <select
          value={localeSelezionato}
          onChange={(e) => {
            setLocaleSelezionato(e.target.value);
            setTokensMap({});
          }}
        >
          <option value="">-- Seleziona --</option>
          {locali.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>

        {datiLocale && (
          <div>
            <p style={{ marginTop: "1rem" }}>
              <strong>{datiLocale.nome}</strong> —{" "}
              {datiLocale.modalitaAsporto
                ? "Modalità Asporto 🍕"
                : `${datiLocale.numeroTavoli || datiLocale.tavoli || 0} tavoli`}
            </p>

            {/* Modalità Asporto: QR unico */}
            {datiLocale.modalitaAsporto ? (
              <div style={{ textAlign: "center", marginTop: "2rem" }}>
                <div
                  style={{
                    display: "inline-block",
                    padding: "1.5rem",
                    backgroundColor: "#FFF3E0",
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "2px solid #FF9800",
                  }}
                >
                  <QRCodeSVG
                    id="qr-asporto"
                    value={buildAsportoUrl()}
                    size={256}
                  />
                  <p
                    style={{
                      marginTop: "0.5rem",
                      fontSize: "0.75rem",
                      color: "#888",
                      wordBreak: "break-all",
                    }}
                  >
                    {buildAsportoUrl()}
                  </p>
                  <p
                    style={{
                      marginTop: "0.5rem",
                      color: "#FF9800",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    🍕 QR Menu Asporto
                  </p>
                  <button
                    onClick={() => {
                      const svg = document.getElementById("qr-asporto");
                      const xml = new XMLSerializer().serializeToString(svg);
                      const svg64 = btoa(unescape(encodeURIComponent(xml)));
                      const imageSrc = "data:image/svg+xml;base64," + svg64;
                      const link = document.createElement("a");
                      link.href = imageSrc;
                      link.download = `${localeSelezionato}_menu_asporto.svg`;
                      link.click();
                    }}
                    style={{
                      marginTop: "1rem",
                      background: "#FF9800",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.7rem 1.5rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: "1rem",
                    }}
                  >
                    📥 Scarica QR Asporto
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={handleDownloadPDF}
                  disabled={loadingPDF || loadingTokens}
                  style={{
                    marginBottom: "1.5rem",
                    background: loadingPDF || loadingTokens ? "#9E9E9E" : "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "0.7rem 1.2rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  {loadingPDF
                    ? "Generazione PDF in corso..."
                    : loadingTokens
                    ? "Caricamento token..."
                    : "Scarica PDF con tutti i QR"}
                </button>

                {/* Selezione sale */}
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "0.85rem 1rem",
                    border: "1px solid #c2c2c2",
                    borderRadius: "10px",
                    background: "#e9ecef",
                    color: "#222",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      marginBottom: "0.6rem",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: "#1a1a1a",
                    }}
                  >
                    Seleziona sale da includere
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.9rem" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#1f1f1f",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!saleSelezionate.principale}
                        onChange={(e) =>
                          setSaleSelezionate({
                            ...saleSelezionate,
                            principale: e.target.checked,
                          })
                        }
                        style={{ transform: "scale(1.1)" }}
                      />
                      Principale
                    </label>
                    {(datiLocale.sale || []).map((s) => (
                      <label
                        key={s.lettera}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: "#1f1f1f",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!saleSelezionate[s.lettera]}
                          onChange={(e) =>
                            setSaleSelezionate({
                              ...saleSelezionate,
                              [s.lettera]: e.target.checked,
                            })
                          }
                          style={{ transform: "scale(1.1)" }}
                        />
                        Sala {s.lettera}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Griglia QR */}
                {loadingTokens ? (
                  <p style={{ marginTop: "2rem", textAlign: "center", color: "#888" }}>
                    ⏳ Caricamento token in corso...
                  </p>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(160px, max-content))",
                      gap: "1.25rem",
                      marginTop: "2rem",
                      justifyContent: "center",
                      justifyItems: "center",
                    }}
                  >
                    {generaListaTavoli().map((tavolo) => {
                      const url = buildQRUrl(tavolo.id);
                      return (
                        <div
                          key={tavolo.id}
                          style={{
                            width: "160px",
                            textAlign: "center",
                            padding: "0.85rem 0.75rem 0.9rem",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "10px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            transition: "transform 0.15s ease, box-shadow 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-3px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 2px 6px rgba(0,0,0,0.1)";
                          }}
                        >
                          <QRCodeSVG
                            id={`qr-${tavolo.id}`}
                            value={url}
                            size={128}
                          />
                          <p
                            style={{
                              marginTop: "0.4rem",
                              color: "#000",
                              fontWeight: "600",
                              fontSize: "0.9rem",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {tavolo.label}
                          </p>
                          {/* URL visibile per debug/verifica */}
                          <p
                            style={{
                              fontSize: "0.6rem",
                              color: "#aaa",
                              wordBreak: "break-all",
                              margin: "0.2rem 0 0.4rem",
                            }}
                          >
                            {url}
                          </p>
                          <button onClick={() => handleDownload(tavolo.id)}>
                            Scarica QR
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default QRCodeGenerator;
